import { ApolloError, isApolloError } from '@apollo/client';
import * as Sentry from '@sentry/react';

interface SentryError extends Record<string, any> {
  readonly message: string;
  readonly status: number;
  readonly query?: string;
}

export interface CustomGraphQLError extends Record<string, any> {
  readonly error?: string;
  readonly status_code?: number;
  readonly query?: string;
}

export interface ExtractedGraphQLError {
  readonly errorMessage: string;
  readonly statusCode: number;
  readonly query: string;
}

type ApolloErrorHandlerCallback = (gqlError: CustomGraphQLError) => void;

export const extractGraphQLError = (error: ApolloError): ExtractedGraphQLError => {
  // Network-only failures may have no graphQLErrors; avoid throwing on [0]
  const graphQLError = error.graphQLErrors?.[0] as CustomGraphQLError | undefined;

  if (!graphQLError) {
    return {
      errorMessage: 'Unknown Error',
      statusCode: 500,
      query: 'Unknown Query',
    };
  }

  const errorMessage = graphQLError.error ?? 'Unknown Error';
  const statusCode = graphQLError.status_code ?? 500;
  const query = graphQLError.query ?? 'Unknown Query';

  return { errorMessage, statusCode, query };
};

/**
 * Chooses the toast title for a failed Apollo mutation
 */
export const toastTitleForMutationError = (error: ApolloError, genericFallback: string): string => {
  const { errorMessage, statusCode } = extractGraphQLError(error);

  /* status_code 400 + a known `error` string → show backend IMEI validation text */
  if (statusCode === 400 && errorMessage !== 'Unknown Error') {
    return errorMessage;
  }

  /* Otherwise → keep the existing generic copy */
  return genericFallback;
};

export const sendToSentry = (error: Error, query?: string, sentryError?: Partial<SentryError>) => {
  if (isApolloError(error)) {
    const { errorMessage, statusCode } = extractGraphQLError(error);
    const defaultSentryError: SentryError = {
      message: errorMessage,
      status: statusCode,
      query: query,
    };
    const finalSentryError = { ...defaultSentryError, ...sentryError };

    Sentry.withScope(scope => {
      scope.setExtras(finalSentryError);
      Sentry.captureException(error);
    });
  } else {
    const defaultSentryError: SentryError = {
      message: error.message,
      status: 500,
    };
    const finalSentryError = { ...defaultSentryError, ...sentryError };

    Sentry.withScope(scope => {
      if (query) {
        scope.setExtra('query', query);
      }
      scope.setExtras(finalSentryError);
      Sentry.captureException(error);
    });
  }
};

export const handleApolloError = (
  error: ApolloError,
  logToSentry = false,
  callback?: ApolloErrorHandlerCallback
) => {
  const gqlError = extractGraphQLError(error);
  if (logToSentry) {
    sendToSentry(error, undefined, { ...gqlError });
  }

  if (callback) {
    callback(gqlError);
  }

  return extractGraphQLError(error);
};
